import styled from '@emotion/styled';
import Container from '@/components/common/elements/Container';
import Text from '@/components/common/elements/Text';
import StyledBox from '@/components/common/elements/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import KakaoChannelAddButton from '@/components/common/organism/KakaoChannelAddButton';
import { MobileRecruitmentCard } from '@/components/domain/recruitment/RecruitmentGrid';
import type { RecruitmentListData } from '@/components/domain/recruitment/types';
import { FORM_COLOR as C } from '@/pages/Seminar/components/form/formStyles';
import type { ClinicSignupDone } from './DentalSignUpForm';

import { designSystem } from '@/styles/theme';

interface SignUpCompleteProps {
    /** 가입 유형 — 치과는 채널 추가를 더 강하게 안내(지원자 등록 알림) */
    signUpType?: 'user' | 'clinic' | '';
    /** 로그인한 뒤 돌아갈 화면(예: 공유받은 AI 대화) — 없으면 로그인 기본 동작(홈) */
    returnUrl?: string | null;
    /** 치과 가입 결과 — 인증 완료면 채용 공고 예시 카드와 [채용 공고 등록하러 가볼까요?] */
    clinicDone?: ClinicSignupDone | null;
}

const ClinicBox = styled.section<{ verified: boolean }>`
    margin-top: 20px;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid ${(p) => (p.verified ? C.primary : C.border)};
    background: ${(p) => (p.verified ? C.primaryCard : '#fff')};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ClinicTitle = styled.p`
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    color: ${C.text};
`;

const ClinicText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${C.sub};
    word-break: keep-all;
`;

const ExampleHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${C.sub};

    span:last-of-type {
        color: ${C.muted};
        font-weight: 500;
    }
`;

const ExampleSlot = styled.div`
    min-width: 0;
    pointer-events: none;
`;

const PrimaryButton = styled.button`
    width: 100%;
    min-height: 52px;
    border: none;
    border-radius: 14px;
    background: ${C.primary};
    color: #fff;
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
`;

const GhostButton = styled(PrimaryButton)`
    border: 1px solid ${C.border};
    background: #fff;
    color: ${C.primaryDeep};
    font-weight: 700;
`;

/** 가입 기본정보로 만든 예시 공고 — 직무·급여는 예시 값(링크 없음) */
const exampleCard = (done: ClinicSignupDone): RecruitmentListData => {
    const parts: string[] = done.address.split(/\s+/).filter(Boolean);
    return {
        id: 0,
        title: '[예시] 치과위생사 정규직 모집',
        clinic: {
            id: 0,
            name: done.clinicName,
            address: done.address,
            city: parts[0] ?? '',
            district: parts[1] ?? '',
            pcThumbnail: done.thumbnail,
            mobileThumbnail: done.thumbnail,
        },
        jobTasks: ['치과위생사'],
        recruitmentTypes: ['정규직'],
        continuousRecruitment: true,
        payChip: '월 300만 원(예시)',
        infoChips: ['월 300만 원(예시)'],
    };
};

const SignUpComplete = ({ signUpType, returnUrl = null, clinicDone = null }: SignUpCompleteProps) => {
    const navigate = useNavigate();
    const isClinic = signUpType === 'clinic';
    const verified: boolean = isClinic && !!clinicDone?.clinicVerified;
    const loginTo = (path: string | null) => navigate(path ? `/login?returnUrl=${encodeURIComponent(path)}` : '/login');
    return (
        <Container display={"block"} gap='0'>
            <StyledBox boxposition='top' width='100%' justifyitems='center' border={`1px solid ${designSystem.colors.primary}`}>
                <CheckCircleOutlineIcon
                    style={{
                        width: '88px',
                        height: '88px',
                        color: designSystem.colors.primary,
                        margin: '12px 0',
                    }}
                />
                <Text fontweight='bold' size='20px'>회원가입 완료</Text>
                <Text>회원가입이 완료되었습니다.</Text>
            </StyledBox>
            {/* 인증된 치과는 주 버튼이 아래 [채용 공고 등록하러 가볼까요?] 하나 — 로그인은 보조 모양(흰 바탕·청록 글자). 그 밖은 지금처럼 진한 [로그인] */}
            <StyledBox
                cursor='pointer'
                justifycontent='center'
                onClick={() => loginTo(returnUrl)}
                boxposition='bottom'
                bgcolor={verified ? '#fff' : designSystem.colors.primary}
                border={verified ? `1px solid ${designSystem.colors.primary}` : undefined}
                style={verified ? { borderTop: `1px solid ${C.line}` } : undefined}
            >
                <Text textcolor={verified ? C.primaryDeep : '#fff'} fontweight='bold'>로그인</Text>
            </StyledBox>

            {isClinic && verified && clinicDone && (
                <ClinicBox verified aria-label="치과 인증 완료">
                    <ClinicTitle>치과 인증이 완료됐어요</ClinicTitle>
                    <ClinicText>이제 채용 공고를 올릴 수 있어요. 공고는 이렇게 보여요.</ClinicText>
                    <ExampleHead><span>채용 공고 예시</span><span>직무·급여는 예시예요</span></ExampleHead>
                    <ExampleSlot aria-hidden="true">
                        <MobileRecruitmentCard recruitment={exampleCard(clinicDone)} isMobile preview hideCounts />
                    </ExampleSlot>
                    <PrimaryButton type="button" onClick={() => loginTo('/recruitments/new')}>채용 공고 등록하러 가볼까요?</PrimaryButton>
                    <GhostButton type="button" onClick={() => loginTo('/ai')}>카페에 올린 공고 붙여 넣어 만들기</GhostButton>
                </ClinicBox>
            )}
            {isClinic && !verified && (
                <ClinicBox verified={false} aria-label="치과 인증 확인 중">
                    <ClinicTitle>치과 인증을 확인하고 있어요</ClinicTitle>
                    <ClinicText>치과 인증이 끝나면 앱 푸시로 알려 드려요. 영업일 기준 1~2일 걸려요.</ClinicText>
                </ClinicBox>
            )}

            {/* 가입완료 카드와 분리된 독립 채널 추가 블록 */}
            <div style={{
                marginTop: 20,
                padding: 20,
                border: '1px solid #FEE500',
                borderRadius: 12,
                background: '#FFFDF0',
            }}>
                <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#191600' }}>
                    카카오톡 알림 받기
                </p>
                <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b5b1a', lineHeight: 1.6 }}>
                    {isClinic
                        ? '채널을 추가해야 지원자 등록 시 카카오톡으로 알림을 드립니다. 꼭 켜주세요!'
                        : '채용·세미나 등 명치사 소식을 카카오톡으로 받아보세요.'}
                </p>
                <KakaoChannelAddButton
                    label={isClinic ? '카카오 채널 추가 (지원자 알림 받기)' : '명치사 카카오 채널 추가'}
                />
            </div>
        </Container>
    );
};

export default SignUpComplete;
